import React from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from '@pankod/refine-antd'
import { useInvalidate, useOne, useUpdate } from '@pankod/refine-core'
import { clsx } from 'clsx'

import {
  Participant,
  ParticipantDetail,
} from '@resources/lms-management/participant-management/type'
import { PROGRAM_STATE } from '@resources/lms-management/program/types'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { TCommonResponse, TLMSCommonError } from 'src/interfaces/common'
import { useWarnIfUnsavedChanges } from 'utils/hooks'
import {
  TPrimerParticipantField,
  TUpdatePrimerParticipantRequest,
  TUpdatePrimerParticipantResponse,
} from './types'
import {
  buildPrimerParticipantRequest,
  getChangedPrimerFields,
  getPrimerParticipantValues,
  JENJANG_OPTIONS,
  PARTICIPANT_TYPE_OPTIONS,
  PRIMER_FIELDS,
  REPRINT_WARNING,
} from './utils'

type TParticipantEditorProps = {
  onAfterClose: () => void
  onClose: () => void
  onSaved: () => void
  participant: Participant
  programId: string
  programStatus: PROGRAM_STATE
  visible: boolean
}

const DISCARD_WARNING =
  'Anda memiliki perubahan yang belum disimpan. Yakin ingin meninggalkan formulir?'
const CUSTOM_JENJANG = '__lainnya__'
const SOURCE_LABELS: Record<ParticipantDetail['source'], string> = {
  PARTICIPANT_CONSOLIDATION_DATA_EMPTY: 'Data kosong',
  PARTICIPANT_CONSOLIDATION_DATA_PRIMARY: 'Primer',
  PARTICIPANT_CONSOLIDATION_DATA_SECONDARY: 'Sekunder',
  PARTICIPANT_CONSOLIDATION_DATA_TERTIARY: 'Tersier',
}

export const ParticipantEditor = ({
  onAfterClose,
  onClose,
  onSaved,
  participant,
  programId,
  programStatus,
  visible,
}: TParticipantEditorProps) => {
  const [rows, setRows] = React.useState<ParticipantDetail[] | null>(null)
  const [values, setValues] = React.useState(() =>
    getPrimerParticipantValues([]),
  )
  const [customJenjang, setCustomJenjang] = React.useState(false)
  const { data, isFetching, isError, refetch } = useOne<
    TCommonResponse<ParticipantDetail[]>,
    TLMSCommonError
  >({
    resource: `programs/${programId}/participant-consolidation/users`,
    id: participant.userId,
    dataProviderName: 'lms',
    queryOptions: { retry: false, refetchOnMount: 'always' },
  })
  const baseline = getPrimerParticipantValues(rows ?? [])

  React.useEffect(() => {
    // Start each edit from a fresh response, then preserve the draft on refetch.
    if (visible && !rows && !isFetching && !isError && data?.data?.data) {
      const initialValues = getPrimerParticipantValues(data.data.data)
      setRows(data.data.data)
      setValues(initialValues)
      setCustomJenjang(
        !!initialValues.jenjang &&
          !JENJANG_OPTIONS.includes(initialValues.jenjang),
      )
    }
  }, [data, isError, isFetching, rows, visible])
  const [reprint, setReprint] = React.useState(false)
  const [pendingRequest, setPendingRequest] =
    React.useState<TUpdatePrimerParticipantRequest | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const submitting = React.useRef(false)
  const invalidate = useInvalidate()
  const { mutateAsync } = useUpdate<
    TUpdatePrimerParticipantResponse,
    TLMSCommonError,
    TUpdatePrimerParticipantRequest
  >()
  const changedFields = getChangedPrimerFields(baseline, values)
  const canReprint = programStatus === PROGRAM_STATE.ENDED
  const isDirty = changedFields.length > 0 || reprint
  const fieldsDisabled = isSubmitting || !!pendingRequest || !visible

  useWarnIfUnsavedChanges(visible && isDirty, DISCARD_WARNING)

  React.useEffect(() => {
    if (!canReprint) {
      setReprint(false)
      setPendingRequest((current) =>
        current?.reprint_certificate ? null : current,
      )
    }
  }, [canReprint])

  const requestClose = () => {
    if (submitting.current || !visible) return
    if (!isDirty || window.confirm(DISCARD_WARNING)) onClose()
  }

  const updateValue = (key: TPrimerParticipantField, value: string) => {
    setValues((current) => ({ ...current, [key]: value }))
  }

  const save = async (request: TUpdatePrimerParticipantRequest) => {
    if (submitting.current || !visible) return
    // Recheck program restrictions, including if status changed during confirmation.
    try {
      buildPrimerParticipantRequest(
        baseline,
        values,
        !!request.reprint_certificate,
        programStatus,
      )
    } catch (error) {
      notification.error({
        message:
          error instanceof Error ? error.message : 'Gagal mengubah data primer',
      })
      return
    }
    submitting.current = true
    setIsSubmitting(true)
    try {
      await mutateAsync({
        resource: `ops-tools/programs/${programId}/participants/${encodeURIComponent(participant.userId)}`,
        id: 'primer',
        values: request,
        dataProviderName: 'lms',
        metaData: { httpMethod: 'patch' },
        mutationMode: 'pessimistic',
        invalidates: [],
        successNotification: false,
        errorNotification: false,
      })
    } catch (error) {
      const details = showErrorNotification(error, 'Gagal mengubah data primer')
      notification.error({
        message: details.message,
        description: details.description,
      })
      submitting.current = false
      setIsSubmitting(false)
      return
    }

    notification.success({ message: 'Data primer partisipan berhasil diubah' })
    void Promise.allSettled([
      invalidate({
        resource: `programs/${programId}/participant-consolidation`,
        dataProviderName: 'lms',
        invalidates: ['list'],
      }),
      invalidate({
        resource: `programs/${programId}/participant-consolidation/users`,
        id: participant.userId,
        dataProviderName: 'lms',
        invalidates: ['detail'],
      }),
    ])
    onSaved()
  }

  const submit = () => {
    if (submitting.current || !visible) return
    try {
      const request = buildPrimerParticipantRequest(
        baseline,
        values,
        canReprint && reprint,
        programStatus,
      )
      setPendingRequest(request)
    } catch (error) {
      notification.error({
        message:
          error instanceof Error ? error.message : 'Gagal mengubah data primer',
      })
    }
  }

  return (
    <Drawer
      title="Ubah Data Primer Partisipan"
      open={visible}
      afterOpenChange={(open) => {
        if (!open) onAfterClose()
      }}
      width={640}
      onClose={requestClose}
      closable={!isSubmitting}
      keyboard={!isSubmitting && !pendingRequest}
      maskClosable={false}
      footer={
        rows ? (
          <Space className="flex justify-end">
            <Button onClick={requestClose} disabled={fieldsDisabled}>
              Batal
            </Button>
            <Button
              type="primary"
              onClick={submit}
              loading={isSubmitting}
              disabled={!changedFields.length || !!pendingRequest || !visible}
            >
              Simpan Perubahan
            </Button>
          </Space>
        ) : null
      }
    >
      {!rows ? (
        isError && !isFetching ? (
          <Alert
            type="error"
            showIcon
            message="Gagal memuat data partisipan"
            action={<Button onClick={() => void refetch()}>Coba Lagi</Button>}
          />
        ) : (
          <Skeleton active />
        )
      ) : (
        <>
          <Descriptions column={1} size="small" className="mb-4" bordered>
            <Descriptions.Item label="User ID">
              {participant.userId}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {participant.email}
            </Descriptions.Item>
          </Descriptions>
          <Form layout="vertical" disabled={fieldsDisabled}>
            {PRIMER_FIELDS.map(({ key, label }) => {
              const source = rows.find((row) => row.label === key)?.source
              return (
                <Form.Item
                  key={key}
                  label={label}
                  htmlFor={`primer-${key}`}
                  extra={
                    source ? (
                      <Tag className="!mt-1">{SOURCE_LABELS[source]}</Tag>
                    ) : undefined
                  }
                >
                  {key === 'participant_type' ? (
                    <Select
                      id={`primer-${key}`}
                      value={values[key] || undefined}
                      placeholder="Pilih tipe partisipan"
                      options={PARTICIPANT_TYPE_OPTIONS.map((value) => ({
                        label: value,
                        value,
                      }))}
                      onChange={(value: string) => updateValue(key, value)}
                    />
                  ) : key === 'jenjang' ? (
                    <>
                      <Select
                        id={`primer-${key}`}
                        className="w-full"
                        value={
                          customJenjang
                            ? CUSTOM_JENJANG
                            : values.jenjang || undefined
                        }
                        placeholder="Pilih jenjang"
                        allowClear
                        options={[
                          ...JENJANG_OPTIONS.map((value) => ({
                            label: value,
                            value,
                          })),
                          { label: 'Lainnya', value: CUSTOM_JENJANG },
                        ]}
                        onChange={(value: string | undefined) => {
                          setCustomJenjang(value === CUSTOM_JENJANG)
                          updateValue(
                            key,
                            value === CUSTOM_JENJANG ? '' : (value ?? ''),
                          )
                        }}
                      />
                      {customJenjang ? (
                        <Input
                          aria-label="Jenjang lainnya"
                          className="mt-2"
                          value={values.jenjang}
                          onChange={(event) =>
                            updateValue(key, event.target.value)
                          }
                          placeholder="Masukkan jenjang"
                        />
                      ) : null}
                    </>
                  ) : (
                    <Input
                      id={`primer-${key}`}
                      value={values[key]}
                      onChange={(event) => updateValue(key, event.target.value)}
                    />
                  )}
                </Form.Item>
              )
            })}
            {canReprint ? (
              <Checkbox
                checked={reprint}
                onChange={(event) => setReprint(event.target.checked)}
              >
                Cetak ulang sertifikat
              </Checkbox>
            ) : null}
            {canReprint && reprint ? (
              <Alert
                className="!mt-4"
                type="warning"
                showIcon
                description={REPRINT_WARNING}
                message="Cetak Ulang Sertifikat"
              />
            ) : null}
          </Form>
        </>
      )}
      <Modal
        title={
          pendingRequest?.reprint_certificate
            ? 'Konfirmasi Perubahan dan Cetak Ulang Sertifikat'
            : 'Konfirmasi Perubahan Data Primer'
        }
        width={760}
        open={visible && !!pendingRequest}
        onCancel={() => {
          if (!submitting.current) setPendingRequest(null)
        }}
        onOk={() => {
          if (pendingRequest) void save(pendingRequest)
        }}
        okText={
          pendingRequest?.reprint_certificate
            ? 'Simpan dan Cetak Ulang'
            : 'Ya, Simpan Perubahan'
        }
        cancelText="Kembali"
        confirmLoading={isSubmitting}
        cancelButtonProps={{ disabled: isSubmitting }}
        closable={!isSubmitting}
        maskClosable={false}
        keyboard={!isSubmitting}
      >
        {pendingRequest?.reprint_certificate ? (
          <Alert
            type="warning"
            showIcon
            description={REPRINT_WARNING}
            className="!mb-4"
            message="Cetak Ulang Sertifikat"
          />
        ) : null}
        <Descriptions
          title="Informasi Partisipan"
          column={1}
          size="small"
          className="mb-4"
          bordered
        >
          <Descriptions.Item label="User ID">
            {participant.userId}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {participant.email}
          </Descriptions.Item>
        </Descriptions>
        <Typography.Title level={5}>Perubahan Data Primer</Typography.Title>
        <Table
          dataSource={PRIMER_FIELDS.filter(({ key }) =>
            Object.hasOwn(pendingRequest?.data ?? {}, key),
          ).map(({ key, label }) => ({
            key,
            label,
            previousValue: baseline[key],
            nextValue: pendingRequest?.data[key] ?? '',
          }))}
          rowKey="key"
          pagination={false}
          tableLayout="fixed"
          bordered
          size="small"
        >
          <Table.Column title="Label" dataIndex="label" width="24%" />
          <Table.Column
            title="Data Sebelumnya"
            dataIndex="previousValue"
            render={(value: string) => (
              <span
                className={clsx(
                  'break-words whitespace-pre-wrap',
                  !value && 'italic text-subdued',
                )}
              >
                {value || '(kosong)'}
              </span>
            )}
          />
          <Table.Column
            title="Diubah Menjadi"
            dataIndex="nextValue"
            render={(value: string) => (
              <span
                className={clsx(
                  'break-words whitespace-pre-wrap',
                  !value && 'italic text-subdued',
                )}
              >
                {value || '(kosong)'}
              </span>
            )}
          />
        </Table>
      </Modal>
    </Drawer>
  )
}
